import React, { useContext, useEffect, useRef, useState } from 'react';
import InfoIcon from '@mui/icons-material/Info';
import cx from 'classnames';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import { DetailsContext } from '@/common/containers/TaskDetails/components/DetailsProvider';
import styles from '@/common/containers/TaskDetails/styles.less';
import { useCurrentDecedentId } from '@/app/providers/DecedentProvider';
import Tooltip from '@/common/utils/bbParser/Tooltip';
import { useTranslation } from 'react-i18next';
import { useSubmitAnswersMutation } from '@/entities/question';
import { useGeneralMetaDataQuery } from '@/entities/meta-data';
import { useLocalizedField } from '@/common/hooks/index';

const IntendedPlan = ({ type }) => {
  const { t } = useTranslation();
  const decedentId = useCurrentDecedentId();
  const { data: metaData = {} } = useGeneralMetaDataQuery();
  const { dropdowns = {} } = metaData;
  const getLocalizedField = useLocalizedField();
  const { intendedPlan: plan, refreshTask } = useContext(DetailsContext);
  const [value, setValue] = useState('');
  const planRef = useRef(plan);
  // true while a plan change is being saved and the task refetched: task data
  // arriving in the meantime (e.g. after a step update) may still hold the old plan
  const isSyncingRef = useRef(false);
  const submitAnswersMutation = useSubmitAnswersMutation({
    onSuccess: async () => {
      try {
        await refreshTask?.();
      } finally {
        isSyncingRef.current = false;
      }
    },
    onError: () => {
      isSyncingRef.current = false;
      if (planRef.current) setValue(planRef.current.value);
    },
  });

  useEffect(() => {
    planRef.current = plan;
    if (plan && !isSyncingRef.current) {
      setValue(plan.value);
    }
  }, [plan]);

  const changePlan = async ({ target: { value: inputValue } }) => {
    const action = !plan.answerId ? 'create' : 'update';

    const payload = {
      taskStepId: plan.taskStepId,
      action,
      value: { plan: inputValue },
      ...(plan.answerId && { answerId: plan.answerId }),
    };

    isSyncingRef.current = true;
    setValue(inputValue);
    submitAnswersMutation.mutate({
      answers: [payload],
      decedentId,
      section: 'main',
      sectionType: 'Task',
    });
  };

  const tooltip = type === 'liabilities' ? { tip147: true } : { tip136: true };
  const options =
    (plan && getLocalizedField(dropdowns, plan.element.type)) ||
    dropdowns?.[plan?.element?.type] ||
    [];

  if (!plan) return null;
  return (
    <div className={styles.infoBlock}>
      <div className={styles.infoBlockHeader}>
        <h5 className={styles.infoBlockHeaderTitle}>{t('Intended plan')}</h5>
        <Tooltip {...tooltip} placement="left-end">
          <InfoIcon />
        </Tooltip>
      </div>
      <div className={cx(styles.infoBlockBody)}>
        <Select
          onChange={changePlan}
          value={value}
          fullWidth
          disabled={submitAnswersMutation.isPending}
        >
          <MenuItem value="none" disabled>
            {t('Select intended plan')}
          </MenuItem>
          {options.map(({ value: itemValue, label }) => (
            <MenuItem key={itemValue} value={itemValue}>
              {label}
            </MenuItem>
          ))}
        </Select>
      </div>
    </div>
  );
};

export default IntendedPlan;
